import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import type { DateValueType } from "react-tailwindcss-datepicker";
import { DateTime } from "luxon";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import {
  UPDATE_EDUCATION_PROGRAM_LEVEL_MUTATION,
  UPDATE_LIFELONG_LEARNING_PROGRAM_LEVEL_MUTATION,
  educationProgramLevelRefetchQueries,
  type LifelongLearningRefetchQuery,
  type UpdateEducationProgramLevelResponse,
  type UpdateLifelongLearningProgramLevelResponse
} from "./mutations";
import type { EducationProgramLevelCatalog } from "./AddEducationProgramLevelModal";

const REMARKS_MAX_LENGTH = 60;

type FormValues = {
  startDate: string;
  endDate: string;
  completeCode: string;
  remarks: string;
};

export type EducationProgramLevelToEdit = {
  completeCode: string | null;
  endDateIso: string | null;
  levelCode: string;
  levelLabel: string;
  remarks: string | null;
  startDateIso: string;
};

interface EditEducationProgramLevelModalProps {
  level: EducationProgramLevelToEdit;
  onClose(): void;
  open: boolean;
  pgaId: string;
  catalog?: EducationProgramLevelCatalog;
  refetchQueries?: LifelongLearningRefetchQuery[];
}

function buildCompletionCodeOptions(currentCode: string | null) {
  const options = [
    { value: "", label: "Please Select" },
    { value: "COMPLETE", label: "Completed" },
    { value: "TERMACPR", label: "Terminated" }
  ];

  const normalized = currentCode?.trim().toUpperCase();
  if (normalized && !options.some((option) => option.value === normalized)) {
    options.push({ value: normalized, label: normalized });
  }

  return options;
}

function buildDefaultValues(level: EducationProgramLevelToEdit): FormValues {
  return {
    startDate: level.startDateIso,
    endDate: level.endDateIso || "",
    completeCode: level.completeCode?.trim().toUpperCase() || "",
    remarks: level.remarks || ""
  };
}

function validateCompletionPair(endDate: string, completeCode: string) {
  const hasDate = Boolean(endDate.trim());
  const hasCode = Boolean(completeCode.trim());

  if (hasDate === hasCode) {
    return true;
  }

  if (hasDate) {
    return "Choose a completion code, or clear the end date too.";
  }

  return "Choose an end date, or clear the completion code too.";
}

export default function EditEducationProgramLevelModal({
  level,
  onClose,
  open,
  pgaId,
  catalog = "PGM",
  refetchQueries
}: EditEducationProgramLevelModalProps) {
  const completionCodeOptions = useMemo(() => buildCompletionCodeOptions(level.completeCode), [level.completeCode]);

  const updateLevelMutation =
    catalog === "LIFELONG_LEARNING"
      ? UPDATE_LIFELONG_LEARNING_PROGRAM_LEVEL_MUTATION
      : UPDATE_EDUCATION_PROGRAM_LEVEL_MUTATION;

  const [updateProgramLevel, { loading: saving }] = useMutation<
    UpdateEducationProgramLevelResponse | UpdateLifelongLearningProgramLevelResponse
  >(updateLevelMutation, {
    refetchQueries: refetchQueries ?? educationProgramLevelRefetchQueries(pgaId),
    awaitRefetchQueries: true
  });

  const { control, handleSubmit, reset, trigger } = useForm<FormValues>({
    defaultValues: buildDefaultValues(level)
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(buildDefaultValues(level));
  }, [level, open, reset]);

  const handleClose = () => {
    reset(buildDefaultValues(level));
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    try {
      const { data: mutationData } = await updateProgramLevel({
        variables: {
          input: {
            pgaId,
            levelCode: level.levelCode,
            originalStartDate: level.startDateIso,
            startDate: values.startDate,
            endDate: values.endDate || null,
            completeCode: values.completeCode || null,
            remarks: values.remarks || null
          }
        }
      });

      const payload =
        catalog === "LIFELONG_LEARNING"
          ? mutationData && "updateLifelongLearningProgramLevel" in mutationData
            ? mutationData.updateLifelongLearningProgramLevel
            : undefined
          : mutationData && "updateEducationProgramLevel" in mutationData
            ? mutationData.updateEducationProgramLevel
            : undefined;

      if (!payload?.success) {
        showErrorSnackbar(payload?.message || "Unable to update education program level.");
        return;
      }

      showSnackbar(payload.message || "Education program level updated");
      handleClose();
    } catch {
      showErrorSnackbar("Unable to update education program level.");
    }
  };

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Edit Level"
      className="w-[520px] h-fit max-h-[90vh]"
      hasFormElements
      displayOverflow
      actions={
        <div className="flex gap-3">
          <Button variant="text" onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit(onSubmit)}
            disabled={saving}
            testId="edit-education-program-level-submit"
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <TextField label="Level" value={level.levelLabel} disabled className="w-full" />

        <Controller
          name="startDate"
          control={control}
          rules={{ required: "Start date is required" }}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <DateSelect
              label="Start Date"
              value={
                value
                  ? {
                      startDate: value,
                      endDate: value
                    }
                  : null
              }
              onChange={(dateValue: DateValueType | string | null) => {
                if (typeof dateValue === "string") return;
                const startDate = dateValue?.startDate;
                onChange(typeof startDate === "string" ? startDate : "");
              }}
              onBlur={onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              error={!!error}
              errorMessage={error?.message}
              data-testid="edit-education-program-level-start-date"
            />
          )}
        />

        <Controller
          name="endDate"
          control={control}
          rules={{
            validate: {
              pairedWithCompleteCode: (value, values) => validateCompletionPair(value, values.completeCode),
              onOrAfterStartDate: (value, values) => {
                if (!value || !values.startDate) {
                  return true;
                }

                const endDate = DateTime.fromISO(value).startOf("day");
                const startDate = DateTime.fromISO(values.startDate).startOf("day");

                if (endDate < startDate) {
                  return "End date must be on or after the start date";
                }

                return true;
              }
            }
          }}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <DateSelect
              label="End Date"
              value={
                value
                  ? {
                      startDate: value,
                      endDate: value
                    }
                  : null
              }
              onChange={(dateValue: DateValueType | string | null) => {
                if (typeof dateValue === "string") return;
                const endDate = dateValue?.startDate;
                onChange(typeof endDate === "string" ? endDate : "");
                void trigger("completeCode");
              }}
              onBlur={onBlur}
              useSingleDate
              useMultiCalendar={false}
              useModal
              allowClear
              error={!!error}
              errorMessage={error?.message}
              data-testid="edit-education-program-level-end-date"
            />
          )}
        />

        <Controller
          name="completeCode"
          control={control}
          rules={{
            validate: (value, values) => validateCompletionPair(values.endDate, value)
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Select
              label="Completion Code"
              value={value}
              options={completionCodeOptions}
              onSelect={(nextValue) => {
                onChange(nextValue);
                void trigger("endDate");
              }}
              error={!!error}
              errorText={error?.message}
              data-testid="edit-education-program-level-complete-cd"
            />
          )}
        />

        <Controller
          name="remarks"
          control={control}
          rules={{
            maxLength: {
              value: REMARKS_MAX_LENGTH,
              message: `Remarks must be ${REMARKS_MAX_LENGTH} characters or fewer`
            }
          }}
          render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
            <TextField
              label="Remarks"
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!error}
              errorText={error?.message}
              className="w-full"
            />
          )}
        />
      </div>
    </Dialog>
  );
}
